import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  DesignationCreateInput,
  DesignationDetails,
  DesignationListFilters,
  DesignationListItem,
  DesignationSearchResult,
  DesignationUpdateInput,
} from '../types/designation';
import { CONFIGURATION_RECORD_INACTIVE } from '../constants/configuration';
import { nextDisplayOrder } from '../utils/displayOrder';
import { getMasterDataStatusLabel } from '../utils/masterDataStatus';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

const DETAILS_PAGE_SIZE = 100;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function requiredId(value: unknown, label: string): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed <= 0) {
    throw Object.assign(new Error(`${label} is missing`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function requiredText(value: unknown, label: string): string {
  const text = textOrNull(value);
  if (!text) {
    throw Object.assign(new Error(`${label} is required`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return text;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeListItem(row: ObjectValue): DesignationListItem | null {
  try {
    const isActive = boolOrNull(row.isActive);
    return {
      designationId: requiredId(row.designationId, 'designationId'),
      designationCode:
        textOrNull(row.designationCode) ?? String(row.designationId),
      designationName: textOrNull(row.designationName) ?? 'Designation',
      description: textOrNull(row.description),
      departmentId: numberOrNull(row.departmentId),
      departmentName: textOrNull(row.departmentName),
      designationLevel: numberOrNull(row.designationLevel),
      displayOrder: numberOrNull(row.displayOrder),
      isManagerial: boolOrNull(row.isManagerial),
      isActive,
      statusLabel: getMasterDataStatusLabel(isActive, textOrNull(row.statusCode)),
      rowVersion: numberOrNull(row.rowVersion),
    };
  } catch {
    return null;
  }
}

function normalizeDetails(row: ObjectValue): DesignationDetails {
  const isActive = boolOrNull(row.isActive);
  return {
    designationId: requiredId(row.designationId, 'designationId'),
    designationCode: requiredText(row.designationCode, 'designationCode'),
    designationName: requiredText(row.designationName, 'designationName'),
    description: textOrNull(row.description),
    departmentId: numberOrNull(row.departmentId),
    departmentName: textOrNull(row.departmentName),
    designationLevel: requiredId(row.designationLevel, 'designationLevel'),
    displayOrder: numberOrNull(row.displayOrder),
    isManagerial: boolOrNull(row.isManagerial) ?? false,
    isActive,
    statusLabel: getMasterDataStatusLabel(isActive, textOrNull(row.statusCode)),
    rowVersion: requiredId(row.rowVersion, 'rowVersion'),
  };
}

function buildListParams(
  page: number,
  pageSize: number,
  filters: DesignationListFilters = {},
) {
  const params: Record<string, string | number | boolean> = { page, pageSize };
  const search = filters.search?.trim();
  if (search) params.search = search;
  if (filters.departmentId && filters.departmentId > 0) {
    params.departmentId = filters.departmentId;
  }
  if (filters.isManagerial === true || filters.isManagerial === false) {
    params.isManagerial = filters.isManagerial;
  }
  if (filters.isActive === true || filters.isActive === false) {
    params.isActive = filters.isActive;
  }
  return params;
}

export async function getDesignations(options: {
  page?: number;
  pageSize?: number;
  filters?: DesignationListFilters;
} = {}): Promise<DesignationSearchResult> {
  const page = options.page && options.page > 0 ? options.page : 1;
  const pageSize =
    options.pageSize && options.pageSize > 0 ? options.pageSize : 20;

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: number;
        pageSize?: number;
        totalRecords?: number;
        totalPages?: number;
      };
    }>
  >(API_ENDPOINTS.DESIGNATIONS.LIST, {
    params: buildListParams(page, pageSize, options.filters),
  });

  const data = response.data.data ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeListItem)
        .filter((item): item is DesignationListItem => item != null)
    : [];
  const pagination = data.pagination ?? {};
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const resolvedPageSize = numberOrNull(pagination.pageSize) ?? pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / resolvedPageSize));

  return {
    items,
    page: numberOrNull(pagination.page) ?? page,
    pageSize: resolvedPageSize,
    totalItems,
    totalPages,
  };
}

/** No dedicated GET-by-id. Search pages until the designation is found. */
export async function getDesignationDetails(
  designationId: number,
): Promise<DesignationDetails> {
  if (!Number.isInteger(designationId) || designationId <= 0) {
    throw Object.assign(new Error('designationId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    const response = await apiClient.get<
      Envelope<{
        items?: ObjectValue[];
        pagination?: { totalPages?: number; totalRecords?: number };
      }>
    >(API_ENDPOINTS.DESIGNATIONS.LIST, {
      params: { page, pageSize: DETAILS_PAGE_SIZE },
    });
    const data = response.data.data ?? {};
    const items = Array.isArray(data.items) ? data.items : [];
    const match = items.find(
      row => numberOrNull(row.designationId) === designationId,
    );
    if (match) return normalizeDetails(match);

    const totalRecords = numberOrNull(data.pagination?.totalRecords) ?? 0;
    totalPages =
      numberOrNull(data.pagination?.totalPages) ??
      Math.max(1, Math.ceil(totalRecords / DETAILS_PAGE_SIZE));
    page += 1;
  }

  throw Object.assign(new Error('Designation was not found.'), {
    code: 'DESIGNATION_NOT_FOUND',
  });
}

export async function createDesignation(
  input: DesignationCreateInput,
): Promise<DesignationDetails> {
  const existing = await getDesignations({ page: 1, pageSize: DETAILS_PAGE_SIZE });
  const body: Record<string, unknown> = {
    designationCode: input.designationCode.trim(),
    designationName: input.designationName.trim(),
    description: emptyToNull(input.description),
    designationLevel: input.designationLevel,
    displayOrder: nextDisplayOrder(existing.items, existing.totalItems),
    isManagerial: input.isManagerial,
  };
  if (input.departmentId && input.departmentId > 0) {
    body.departmentId = input.departmentId;
  }

  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.DESIGNATIONS.CREATE,
    body,
  );
  return normalizeDetails({
    ...body,
    isActive: true,
    rowVersion: 1,
    ...(response.data.data ?? {}),
  });
}

export async function updateDesignation(
  designationId: number,
  input: DesignationUpdateInput,
): Promise<DesignationDetails> {
  if (!Number.isInteger(designationId) || designationId <= 0) {
    throw Object.assign(new Error('designationId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const body: Record<string, unknown> = {
    designationName: input.designationName.trim(),
    description: emptyToNull(input.description),
    designationLevel: input.designationLevel,
    displayOrder: input.displayOrder ?? 0,
    isManagerial: input.isManagerial,
    isActive: input.isActive,
    rowVersion: input.rowVersion,
  };
  if (input.departmentId && input.departmentId > 0) {
    body.departmentId = input.departmentId;
  }

  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.DESIGNATIONS.UPDATE(designationId),
    body,
  );
  return normalizeDetails({
    designationId,
    designationCode: '—',
    ...body,
    ...(response.data.data ?? {}),
  });
}

export async function deactivateDesignation(
  item: DesignationDetails,
): Promise<DesignationDetails> {
  return updateDesignation(item.designationId, {
    departmentId: item.departmentId,
    designationName: item.designationName,
    description: item.description,
    designationLevel: item.designationLevel,
    displayOrder: item.displayOrder ?? 0,
    isManagerial: item.isManagerial,
    isActive: CONFIGURATION_RECORD_INACTIVE,
    rowVersion: item.rowVersion,
  });
}
